'use client';

import type { ReactNode } from 'react';

import { Trash } from 'lucide-react';
import Dropdown from '@/app/components/ui/Dropdown';
import Icon from '@/app/components/ui/Icon';

export type TableRowAction = {
  value: string;
  label: string;
  type?: 'delete' | 'restore';
  icon?: ReactNode;
  onSelect: () => void;
};

type TableRowActionsProps = {
  actions: TableRowAction[];
  ariaLabel: string;
  onOpenChange?: (open: boolean) => void;
  busy?: boolean;
  disabled?: boolean;
};

export default function TableRowActions({
  onOpenChange,
  actions,
  ariaLabel,
  busy = false,
  disabled = false,
}: TableRowActionsProps) {
  return (
    <div className="flex w-full justify-end" onClick={event => event.stopPropagation()}>
      {busy ? (
        <span className="flex size-7 items-center justify-center">
          <Icon name="loader-2-fill" className="size-5 animate-spin text-muted-foreground" />
        </span>
      ) : (
        <Dropdown
          matchTriggerWidth={false}
          contentClassName="min-w-[120px]"
          menuItemClassName="whitespace-nowrap"
          dropdownMenuProps={{ modal: false, onOpenChange }}
          dropdownMenuContentProps={{ align: 'end' }}
          disabled={disabled}
          menuItems={actions.map(action => ({
            label: (
              <span className="flex w-full items-center gap-2 font-medium text-muted-foreground">
                {action.icon ??
                  (action.type === 'restore' ? (
                    <Icon name="refresh-line" size={5} />
                  ) : action.type === 'delete' ? (
                    <Trash className="size-5" />
                  ) : null)}
                {action.label}
              </span>
            ),
            value: action.value,
          }))}
          onItemClick={value => actions.find(action => action.value === value)?.onSelect()}
        >
          <button
            type="button"
            className="rounded p-1 outline-none focus:ring-2 focus:ring-primary-500"
            aria-label={ariaLabel}
            title={ariaLabel}
          >
            <Icon name="more" className="size-5 text-muted-foreground" />
          </button>
        </Dropdown>
      )}
    </div>
  );
}
